/**
 * Portal Manta · App principal (v29 modular)
 * v29 · 2026-05-11
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import * as XLSX from 'xlsx';

// Tokens
import { M, F, STATUS } from './tokens/theme.js';

// Dados modularizados
import { UFS_BR } from './data/ufs-br.js';
import { BANCO_CHUVA_UF } from './data/banco-chuva-uf.js';
import { SHAREPOINT_FAVS } from './data/sharepoint-favs.js';
import { KPIS_QUALI_SCHEMA, KPIS_QUANTI_SCHEMA } from './data/kpis-schemas.js';
import { PAYLOAD_LOTE1_PR } from './data/payload-lote1-pr.js';
import { ESTUDOS_INIT } from './data/estudos-init.js';
import { DADOS } from './data/lotes-pr.js';
import { SICRO_DESC } from './data/sicro-desc.js';
import { PER_EXTRACTED } from './data/per-extracted.js';
import { CONSOL_FIN } from './data/consol-fin.js';
import { COR } from './data/cor.js';
import { PORTAL_METADATA } from './data/portal-metadata.js';
import { CAPITULOS_M, CAP_BY_ID, NUM_BY_ID, TOUR_M } from './data/capitulos.js';

// Utils
import { cossenoVec } from './utils/cosseno.js';
import { wrapHtmlManta } from './utils/html-wrap.js';
import { downloadBlob, workbookToBlob } from './utils/xlsx-helpers.js';

// Hooks (disponíveis para uso futuro)
import { useLocalStorage } from './hooks/useLocalStorage.js';
import { useStudyState } from './hooks/useStudyState.js';

// Componentes shared (disponíveis para uso futuro)
import { BadgeFonte } from './components/shared/BadgeFonte.jsx';
import { HeaderArtefato, SubsecaoM, TabelaM, PreM, CalloutM, WatermarkManta, Fonte, DadoNumerico } from './components/shared/ArtefatoHelpers.jsx';

// Componentes de página
import { AluciGuardPlaceholderPage } from './components/aluci-guard/AluciGuardPage.jsx';

// Componentes extraídos em FASE 2
import { FonteKpiBadge } from './components/novo-estudo/FonteKpiBadge.jsx';
import { CelulaEdit } from './components/novo-estudo/CelulaEdit.jsx';
import { LinhaTabelaKPI } from './components/novo-estudo/LinhaTabelaKPI.jsx';
import { TabelaKPIs } from './components/novo-estudo/TabelaKPIs.jsx';
import { LinhaRelatorio } from './components/relatorios/LinhaRelatorio.jsx';
import { GlossarioPage } from './components/pages/GlossarioPage.jsx';
import { GuiaPage } from './components/pages/GuiaPage.jsx';
import { Card1Qualitativo } from './components/novo-estudo/Card1Qualitativo.jsx';
import { RelatoriosStandalonePage } from './components/relatorios/RelatoriosStandalonePage.jsx';
import { HomePage } from './components/pages/HomePage.jsx';
import { TourPanelM } from './components/sidebar/TourPanelM.jsx';
import { SubSidebar } from './components/sidebar/SubSidebar.jsx';

// Componentes extraídos em FASE 3
import { FatoresQualLotePage } from './components/pages/FatoresQualLotePage.jsx';
import { ComparativoLotesPage } from './components/pages/ComparativoLotesPage.jsx';
import { ServicosRodPage } from './components/pages/ServicosRodPage.jsx';
import { DashFinPage } from './components/pages/DashFinPage.jsx';
import { DashPage } from './components/pages/DashPage.jsx';
import { FinPage } from './components/pages/FinPage.jsx';
import { TipoConsolPage } from './components/pages/TipoConsolPage.jsx';
import { TipPage } from './components/pages/TipPage.jsx';
import { OrcamentoRevisaoTab } from './components/pages/OrcamentoRevisaoTab.jsx';
import { OrcParamPage } from './components/pages/OrcParamPage.jsx';
import { CronoPage } from './components/pages/CronoPage.jsx';
import { ObrasConsolPage } from './components/pages/ObrasConsolPage.jsx';
import { GestaoPage } from './components/pages/GestaoPage.jsx';
import { DiagnosticoPage } from './components/pages/DiagnosticoPage.jsx';
import { BotoeiraAcoes } from './components/novo-estudo/BotoeiraAcoes.jsx';
import { SharePointPicker } from './components/novo-estudo/SharePointPicker.jsx';


// Componentes extraídos em FASE 4
import { Card2Importar } from './components/novo-estudo/Card2Importar.jsx';
import { Card3KPIs } from './components/novo-estudo/Card3KPIs.jsx';
import { Card4Similaridade } from './components/novo-estudo/Card4Similaridade.jsx';
import { Card5Orcamento } from './components/novo-estudo/Card5Orcamento.jsx';
import { Card6Salvar } from './components/novo-estudo/Card6Salvar.jsx';
import { NovoOrcAdicionarTab } from './components/pages/NovoOrcAdicionarTab.jsx';
import { OrcamentoConsolidadoPage } from './components/pages/OrcamentoConsolidadoPage.jsx';
import { CriterioSimilaridadePage } from './components/pages/CriterioSimilaridadePage.jsx';
import { BenchmarkingPage } from './components/pages/BenchmarkingPage.jsx';
import { ApiStackPage } from './components/pages/ApiStackPage.jsx';
import { KnowledgePage } from './components/pages/KnowledgePage.jsx';
import { AluciGuardPage } from './components/aluci-guard/AluciGuardPage2.jsx';
import { AgentesCatalogoPage } from './components/pages/AgentesCatalogoPage.jsx';
import { CatalogoTabInterno } from './components/pages/CatalogoTabInterno.jsx';
import { ConhecimentoConsolidadoPage } from './components/pages/ConhecimentoConsolidadoPage.jsx';
import { AdicionarBasePage } from './components/pages/AdicionarBasePage.jsx';
import { AjudaPage } from './components/pages/AjudaPage.jsx';
import { SumarioOficialPage } from './components/pages/SumarioOficialPage.jsx';
import { AgenteIAPage } from './components/pages/AgenteIAPage.jsx';
import { ExtractorPage } from './components/pages/ExtractorPage.jsx';
import { CapaPage } from './components/pages/CapaPage.jsx';
import { ContextoLotePage } from './components/pages/ContextoLotePage.jsx';
import { GuiaGlossPage } from './components/pages/GuiaGlossPage.jsx';

// Componentes extraídos em FASE 5
import { RelatoriosPage } from './components/relatorios/RelatoriosPage.jsx';

// Componentes extraídos em FASE 6
import { DocsPage } from './components/pages/DocsPage.jsx';

// Componente extraído em FASE 8
import { NovoEstudoFluxoPage } from './components/novo-estudo/NovoEstudoFluxoPage.jsx';

// Componentes extraídos em FASE 9 (AST)
import { KbComparativo } from './components/knowledge/KbComparativo.jsx';
import { KbSimul } from './components/knowledge/KbSimul.jsx';
import { KbDataLayer } from './components/knowledge/KbDataLayer.jsx';
import { KbCurated } from './components/knowledge/KbCurated.jsx';
import { KbRepo } from './components/knowledge/KbRepo.jsx';
import { KbRag } from './components/knowledge/KbRag.jsx';
import { KbNormas } from './components/knowledge/KbNormas.jsx';
import { KbPrompts } from './components/knowledge/KbPrompts.jsx';
import { KbIngest } from './components/knowledge/KbIngest.jsx';

// Componentes extraídos em FASE 9 (AST)
import { TipoRodoviaPageV2 } from './components/lotes-tabs/TipoRodoviaPageV2.jsx';
import { ObrasPerOrig } from './components/lotes-tabs/ObrasPerOrig.jsx';
import { ObrasTotaisTab } from './components/lotes-tabs/ObrasTotaisTab.jsx';
import { ObrasRodKpisTab } from './components/lotes-tabs/ObrasRodKpisTab.jsx';
import { ObrasPorRodTab } from './components/lotes-tabs/ObrasPorRodTab.jsx';
import { ObrasComposicoesTab } from './components/lotes-tabs/ObrasComposicoesTab.jsx';
import { ComparaTab } from './components/lotes-tabs/ComparaTab.jsx';
import { ParamDfTab } from './components/lotes-tabs/ParamDfTab.jsx';
import { MacroTab } from './components/lotes-tabs/MacroTab.jsx';
import { EstruturaTab } from './components/lotes-tabs/EstruturaTab.jsx';
import { MacroEstTab } from './components/lotes-tabs/MacroEstTab.jsx';
import { ObrasConsolPorRodovia } from './components/lotes-tabs/ObrasConsolPorRodovia.jsx';
import { SectionCollapse } from './components/lotes-tabs/SectionCollapse.jsx';

// Componentes extraídos em FASE 9 (AST)
import { RetigSchematic } from './components/lotes-vis/RetigSchematic.jsx';
import { RodoviaContexto } from './components/lotes-vis/RodoviaContexto.jsx';
import { MatrizDisciplinas } from './components/lotes-vis/MatrizDisciplinas.jsx';
import { MatrizTiposObra } from './components/lotes-vis/MatrizTiposObra.jsx';
import { RodoviaDecomp } from './components/lotes-vis/RodoviaDecomp.jsx';
import { EstruturasTecnicasPanel } from './components/lotes-vis/EstruturasTecnicasPanel.jsx';
import { TipoObraMatrix } from './components/lotes-vis/TipoObraMatrix.jsx';
import { TipoObraConsol } from './components/lotes-vis/TipoObraConsol.jsx';

// Componentes extraídos em FASE 9 (AST)
import { getExtractorMock } from './components/extractor/getExtractorMock.jsx';
import { RoadStrip } from './components/extractor/RoadStrip.jsx';
import { RoadDataCard } from './components/extractor/RoadDataCard.jsx';
import { RetigLegenda } from './components/extractor/RetigLegenda.jsx';
import { RetigGapPanel } from './components/extractor/RetigGapPanel.jsx';
import { TopBarApp } from './components/sidebar/TopBarApp.jsx';
import { MainNavSidebar } from './components/sidebar/MainNavSidebar.jsx';
import { FormacoesSidebar } from './components/sidebar/FormacoesSidebar.jsx';
import { PagesSidebar } from './components/sidebar/PagesSidebar.jsx';
import { Breadcrumb } from './components/sidebar/Breadcrumb.jsx';
import { LoteSummaryStrip } from './components/sidebar/LoteSummaryStrip.jsx';
import { BackendStatus } from './components/sidebar/BackendStatus.jsx';
import { AgenteRunner } from './components/agentes/AgenteRunner.jsx';

// Componentes extraídos em FASE 9 (AST)
import { FonteModal } from './components/charts/FonteModal.jsx';
import { KPICard } from './components/charts/KPICard.jsx';
import { StackedBar } from './components/charts/StackedBar.jsx';
import { PluviChart } from './components/charts/PluviChart.jsx';
import { Gauge } from './components/charts/Gauge.jsx';
import { PavCross } from './components/charts/PavCross.jsx';
import { OAECross } from './components/charts/OAECross.jsx';
import { PassCross } from './components/charts/PassCross.jsx';
import { MEFvsMantaTable } from './components/charts/MEFvsMantaTable.jsx';
import { ConclusoesBox } from './components/charts/ConclusoesBox.jsx';
import { AccordionCard } from './components/charts/AccordionCard.jsx';
import { BotaoAvancar } from './components/charts/BotaoAvancar.jsx';
import { AjusteInput } from './components/charts/AjusteInput.jsx';
import { CapexHero } from './components/charts/CapexHero.jsx';

// Componentes extraídos em FASE 9 (AST)
import { agComparador } from './components/agentes/agComparador.jsx';
import { agConclusao } from './components/agentes/agConclusao.jsx';
import { agNarrativaAI } from './components/agentes/agNarrativaAI.jsx';
import { agColetorKPI } from './components/agentes/agColetorKPI.jsx';
import { agFiltroGeo } from './components/agentes/agFiltroGeo.jsx';
import { agSimilarCalc } from './components/agentes/agSimilarCalc.jsx';
import { agGeradorOrc } from './components/agentes/agGeradorOrc.jsx';
import { agValidadorPreenchimento } from './components/agentes/agValidadorPreenchimento.jsx';
import { agAluciGuardABNT } from './components/agentes/agAluciGuardABNT.jsx';
import { agAluciGuardURL } from './components/agentes/agAluciGuardURL.jsx';
import { agAluciGuardSICRO } from './components/agentes/agAluciGuardSICRO.jsx';
import { aluciAuditarTexto } from './components/agentes/aluciAuditarTexto.jsx';
import { executarPipeline } from './components/agentes/executarPipeline.jsx';

// Componentes extraídos em FASE 9 (AST)
import { normalizarFonte } from './components/agentes/normalizarFonte.jsx';
import { auditarFontesEstrutura } from './components/agentes/auditarFontesEstrutura.jsx';
import { importarExcelKpis } from './components/agentes/importarExcelKpis.jsx';
import { detectarFormatoJSON } from './components/agentes/detectarFormatoJSON.jsx';
import { extrairKpisDoFormatoManta } from './components/agentes/extrairKpisDoFormatoManta.jsx';
import { extrairKpisDoFormatoRaso } from './components/agentes/extrairKpisDoFormatoRaso.jsx';
import { extrairKpisDoPortalV26 } from './components/agentes/extrairKpisDoPortalV26.jsx';
import { importarJSONkpisAvancado } from './components/agentes/importarJSONkpisAvancado.jsx';
import { mesclarPreset } from './components/agentes/mesclarPreset.jsx';
import { abrirParaImprimir } from './components/agentes/abrirParaImprimir.jsx';
import { htmlTabelaKPIs } from './components/agentes/htmlTabelaKPIs.jsx';
import { featuresDoLote } from './components/agentes/featuresDoLote.jsx';
import { featuresDoExtra } from './components/agentes/featuresDoExtra.jsx';
import { calcPraticabilidade } from './components/agentes/calcPraticabilidade.jsx';
import { getQtdObra } from './components/agentes/getQtdObra.jsx';

// Componentes extraídos em FASE 9 (AST)
import { ESTRUTURAS_TIPOLOGIAS } from './data/estruturas-tipologias.js';
import { PRESETS_NOVO_ESTUDO } from './data/presets-novo-estudo.js';
import { KB_ANALISES } from './data/kb-analises.js';
import { CONTEXTO_LOTES } from './data/contexto-lotes.js';
import { RODOVIAS_LOTE } from './data/rodovias-lote.js';
import { NORMAS_ABNT_NBR } from './data/normas-abnt-nbr.js';
import { PIPELINES } from './data/pipelines.js';
import { CONCLUSOES_LOTE } from './data/conclusoes-lote.js';
import { GRUPOS_CONSOL } from './data/grupos-consol.js';
import { SP_API } from './data/sp-api.js';
import { KB_CURATED_COMPOSICOES } from './data/kb-curated-composicoes.js';
import { KB_REPO } from './data/kb-repo.js';
import { LEIS_FEDERAIS_BR } from './data/leis-federais-br.js';
import { AGENTES_CATALOG } from './data/agentes-catalog.js';
import { KB_NORMAS } from './data/kb-normas.js';
import { LOCAL_FILES } from './data/local-files.js';
import { PAL_DEV } from './data/pal-dev.js';
import { TIPO_OBRA_MAP } from './data/tipo-obra-map.js';
import { KB_TABS } from './data/kb-tabs.js';
// ═══════════════════════════════════════════════════════════════════
//  PORTAL MANTA — LOTES PR · v22
//  Otimizações:
//   • useMemo/useEffect adicionados (memoização de filtros e totais)
//   • Helpers consolidados (fmtBRL, fmtNum, fmtPct, sumBy, sigKey)
//   • Tabela SICRO_DESC dedup. (descrições em mapa central)
//   • Objeto S de estilos compartilhados
//   • ConsolFinPage memoizada (rows/totais)
//   • v7: ObrasPage com sidebar vertical numerada (5 sub-abas)
//   • v7: rastreabilidade OBRAS-01..OBRAS-05 + 562 itens detalhados + 268 composicoes
//   • v7: MantaPage (aba 06) com sidebar vertical · MANTA-01..MANTA-04
//   • v8: CONSOL_FIN compactado (puc removido, campos curtos) — < 500 KB para artefato
//   • v9: API client opcional (Fase 2/3 backend Express+SQLite + agentes Anthropic)
//   • v9: BackendStatus indicator + AgenteRunner para chamar agentes via REST
//   • v20: ContextoLotePage — KPIs físicos + conversões MEF (m²)
//   • v20: praticabilidade 0–100 com 4 sub-índices · rastreabilidade modal
//   • v21: decomposição por rodovia (km, km urbano, CAPEX, R$/km, driver principal)
//   • v21: caixa de conclusões — drivers de custo principais por lote (narrativa)
//   • v21: Estruturas Técnicas em TipoConsolPage — seções transversais SVG
//   • v21: pavimento, OAE, passarela com comparativo MEF vs Manta
//   • v21: consumo de material por km (CBUQ, brita, aço CA-50, asfalto, cimento)
//   • v22: rename "Dados Manta" → "Orçamento Paramétrico Manta" (chave manta→orcparam)
//   • v22: nova seção A "Resumo do Orçamento Paramétrico" no topo da página
//   • v22: nova aba dedicada "Guia & Glossário" entre Serviços×Rodovias e Tipologias
//   • v22: decomposição por TIPO DE OBRA na ContextoLotePage (consolidado + matriz rodovia × tipo)
//   • v22.1: CapaPage reorganizada — preserva hero banner com imagem + logo Motiva
//            e adiciona índice com sumário inicial + abas verticais à esquerda
//            (Sumário · Guia · Glossário · Lote 3·4·5 · KB Curada · Novo Orçamento · KPIs)
// ═══════════════════════════════════════════════════════════════════

// ── API Client (Fase 2/3 backend) ──────────────────────────────
// Funciona com ou sem backend. Se backend rodando em http://localhost:3001, sincroniza dados.
const API_BASE = (typeof window !== 'undefined' && window.MANTA_API_BASE) || 'http://localhost:3001';
const API = {
  base: API_BASE,
  enabled: false,
  async ping() {
    try {
      const r = await fetch(API_BASE + '/api/health', { cache: 'no-store' });
      if (!r.ok) return null;
      const j = await r.json();
      this.enabled = true;
      return j;
    } catch { this.enabled = false; return null; }
  },
  async get(path) {
    const r = await fetch(API_BASE + path);
    if (!r.ok) throw new Error('GET '+path+' → '+r.status);
    return r.json();
  },
  async post(path, body) {
    const r = await fetch(API_BASE + path, {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify(body || {}),
    });
    if (!r.ok) throw new Error('POST '+path+' → '+r.status);
    return r.json();
  },
  async put(path, body) {
    const r = await fetch(API_BASE + path, {
      method: 'PUT', headers: {'Content-Type':'application/json'},
      body: JSON.stringify(body || {}),
    });
    if (!r.ok) throw new Error('PUT '+path+' → '+r.status);
    return r.json();
  },
};


// ── HELPERS ─────────────────────────────────────────────────
// fmtBRL e fmtPct ja existem mais abaixo (declarados no codigo original do v3).
const fmtNum = (n,d=2)=> (n||0).toLocaleString('pt-BR',{minimumFractionDigits:d,maximumFractionDigits:d});
const sumBy  = (arr,k)=> (arr||[]).reduce((s,x)=>s+(x?.[k]||0),0);
const sigKey = (lote)=> ((lote?.sig||'')+'').replace(/\D/g,'');
// Filtro obras de ampliação (excluí TI, REC, MANUT, Trabalhos Iniciais, etc.)
const isAmpliacao = (it)=>{
  if(!it) return false;
  // 1) tipologias com campo 'g'
  if(it.g){
    const g = it.g.toLowerCase();
    return g.includes('amplia') || g.includes('oae') || g.includes('interse');
  }
  // 2) por código (A-prefixed = ampliação no PER/MEF do edital PR)
  const c = (it.cod||it.codigo||'').toString();
  if(/^A\d/i.test(c)) return true;
  // 3) por descrição (filtra negativos óbvios)
  const desc = (it.desc||it.descricao||it.l||'').toLowerCase();
  if(/(trabalhos iniciais|recupera|manuten|conserva|passivo ambient|reabilita|reforma)/.test(desc)) return false;
  // se nada bate, manter (default permissive)
  return true;
};
const AmpliBadge = ()=>(<span style={{...S.pillBase,background:V.bl,color:'#fff',marginLeft:8}}>OBRAS DE AMPLIAÇÃO</span>);


// ══════════════════════════════════════════════
//  CONSOLIDADO FINANCEIRO SICRO c/ BDI · L3·L4·L5 (importado de consolidado_financeiro.html)
// ══════════════════════════════════════════════


const fmtV=v=>{if(v==null)return'-';if(v>=1000)return'R$ '+(v/1000).toFixed(2).replace('.',',')+'B';if(v>=1)return'R$ '+v.toFixed(1).replace('.',',')+'M';return'R$ '+(v*1000).toFixed(0)+'K';};
const fmtPct=d=>d==null?'-':((d*100).toFixed(2).replace('.',',')+' %');
const pct=(v,t)=>t?((v/t)*100).toFixed(1).replace('.',',')+' %':'0 %';
const fmtMi=v=>v==null?'-':'R$ '+(v/1e6).toFixed(2).replace('.',',')+'M';
const fmtBR=v=>{if(v==null||isNaN(v))return'—';return'R$ '+Number(v).toLocaleString('pt-BR',{maximumFractionDigits:0});};
const fmtMil=v=>{if(v==null||isNaN(v))return'—';const x=Number(v);if(x>=1e9)return'R$ '+(x/1e9).toFixed(2).replace('.',',')+' bi';if(x>=1e6)return'R$ '+(x/1e6).toFixed(1).replace('.',',')+' mi';if(x>=1e3)return'R$ '+(x/1e3).toFixed(0)+' k';return'R$ '+x.toFixed(0);};

const V={
  or:'#D4682A',orp:'#FDF0E8',orm:'#F5DCC8',
  br:'#1E2A4A',br2:'#2D3F6B',
  g0:'#F8F9FC',g1:'#EEF1F8',g2:'#DDE2EE',g3:'#C4CBDD',g4:'#8A93A8',g5:'#5A6478',g7:'#1E2A4A',
  gn:'#1B7A50',gnb:'#EBF7F1',
  bl:'#2057A8',blb:'#EEF3FC',
  nv:'#1E2A4A',nv2:'#16213A',nv3:'#0D1526',
  gr:'#8A93A8',b1:'#DDE2EE',
};

// ── STYLE TOKENS (compartilhados) ───────────────────────────
const S = {
  page:    {padding:'16px 20px',overflowY:'auto',flex:1},
  h1:      {fontSize:15,fontWeight:700,color:V.br,marginBottom:10,letterSpacing:'-0.3px'},
  h2:      {fontSize:13,fontWeight:700,color:V.br,marginBottom:6},
  sub:     {fontSize:11,color:V.g4,marginBottom:14},
  card:    {background:'#fff',border:'1px solid '+V.g2,borderRadius:10,boxShadow:'0 1px 4px rgba(30,42,74,.06)'},
  cardP:   {padding:'12px 14px',background:'#fff',border:'1px solid '+V.g2,borderRadius:10,boxShadow:'0 1px 4px rgba(30,42,74,.06)'},
  thRow:   {position:'sticky',top:0,background:V.g0,zIndex:1},
  trLine:  {borderBottom:'1px solid '+V.g1},
  tfootRow:{background:V.g0,borderTop:'2px solid '+V.g2},
  inputBase:{padding:'7px 10px',fontSize:11.5,border:'1px solid '+V.g2,borderRadius:6,background:'#fff'},
  pillBase:{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,letterSpacing:.3},
};


// ═══════════════════════════════════════════════════════════════════════════
// MANTA · ARQUITETURA OFICIAL (PK_07 · skill padrao-manta · arquiteto-manta-manual)
// ═══════════════════════════════════════════════════════════════════════════

// ── Paleta oficial Manta (skill padrao-manta) ───────────────────────────


// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// 📚 PADRÃO 12.2 · FONTES & RASTREABILIDADE
// Helpers para normalizar e exibir fontes em formato canônico Manta
// ═══════════════════════════════════════════════════════════════════════════

// ── Normalização (compatibilidade com formato antigo) ──────────────────
// O CONTEXTO_LOTES usa {doc, pag, data, aut} (formato legado interno).
// Padrão canônico Manta (Cap 12.2) é {fonte, fonteData, fonteLocal, fonteUrl, fonteAutor}.
// Esta função aceita ambos e devolve formato canônico.


// ═══════════════════════════════════════════════════════════════════════════
// FIM PADRÃO 12.2 HELPERS
// ═══════════════════════════════════════════════════════════════════════════

// FIM FOUNDATION VISUAL
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// PORTAL · ESTRUTURA E TOUR (Padrão 12.5 · obrigatório)
// ═══════════════════════════════════════════════════════════════════════════

// ── Mapeamento NUM_BY_ID (id de seção → número canônico) ────────────────

// ── Capítulos romanos (agrupadores) ─────────────────────────────────────

// ── Sequência do tour (Padrão 12.5) ─────────────────────────────────────

// ── TourPanel (sticky direita · Padrão 12.5) ────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// FIM ESTRUTURA E TOUR
// ═══════════════════════════════════════════════════════════════════════════


const NAV=[{k:'dashfin',icon:'◈',label:'Consolidado do Lote'},{k:'contexto',icon:'◉',label:'Contexto do Lote'},{k:'servrod',icon:'⌘',label:'Serviços × Rodovias'},{k:'guiagloss',icon:'📘',label:'Guia & Glossário'},{k:'tipoconsol',icon:'⊞',label:'Tipologias × Rodovia'},{k:'obrasconsol',icon:'◆',label:'Obras × Rodovia'},{k:'orcparam',icon:'◇',label:'Orçamento Paramétrico Manta'},{k:'docs',icon:'▤',label:'Documentos'},{k:'agente',icon:'🧠',label:'Inteligência · Agente Orquestrador'}];

// ══════════════════════════════════════════════
//  OBRAS × RODOVIA — quantitativos do PER por rodovia e tipo
// ══════════════════════════════════════════════
const OBRAS_ROD={"3":{"sistema":[{"rod":"BR-376","ext":337.6},{"rod":"PR-445","ext":95.8},{"rod":"PR-323","ext":60.64},{"rod":"BR-369","ext":28.88},{"rod":"PR-090","ext":27.28},{"rod":"PR-170","ext":11.65},{"rod":"BR-373","ext":7.9}],"duplicacoes":[{"rod":"PR-323","trechos":6,"km":57.89},{"rod":"BR-376","trechos":7,"km":54.16},{"rod":"PR-170","trechos":1,"km":11.65},{"rod":"PR-445","trechos":1,"km":8.92}],"oaes":[{"rod":"BR-376","n":82},{"rod":"PR-445","n":24},{"rod":"PR-323","n":18},{"rod":"Cont. Apucarana","n":17},{"rod":"Cont. Leste PG","n":14},{"rod":"Cont. Norte PG","n":12},{"rod":"Cont. Califórnia","n":6},{"rod":"BR-369","n":4},{"rod":"PR-170","n":4},{"rod":"PR-090","n":3}],"diamantes":[{"rod":"PR-323","n":5},{"rod":"BR-376","n":4},{"rod":"BR-369","n":2},{"rod":"PR-445","n":2},{"rod":"Cont. Leste PG","n":2}],"trombetas":[{"rod":"Cont. Leste PG","n":3},{"rod":"PR-323","n":2},{"rod":"Cont. Apucarana","n":2},{"rod":"Cont. Califórnia","n":2},{"rod":"BR-369","n":1},{"rod":"BR-376","n":1},{"rod":"PR-170","n":1}],"rotatorias":[],"parclos":[{"rod":"BR-376","n":44},{"rod":"PR-323","n":8},{"rod":"Cont. Apucarana","n":2},{"rod":"Cont. Leste PG","n":2},{"rod":"Cont. Norte PG","n":2},{"rod":"BR-369","n":1},{"rod":"PR-170","n":1},{"rod":"PR-445","n":1}],"passarelas":[{"rod":"BR-376","n":11},{"rod":"PR-445","n":6},{"rod":"BR-369","n":3},{"rod":"PR-090","n":1},{"rod":"PR-323","n":1}],"marginais":[{"rod":"PR-323","km":2.83,"n":4},{"rod":"PR-090","km":2.29,"n":1},{"rod":"BR-376","km":1.6,"n":1}],"ciclovias":[{"rod":"PR-445","n":20,"km":7.76},{"rod":"BR-376","n":25,"km":6.75},{"rod":"Cont. Leste PG","n":1,"km":5.3},{"rod":"BR-369","n":6,"km":2.85},{"rod":"PR-090","n":2,"km":0.0},{"rod":"PR-323","n":6,"km":1.46},{"rod":"PR-170","n":1,"km":0.0}],"faixas":[{"rod":"PR-445","n":6,"km":24.61}],"contornos":[{"nome":"Cont. Apucarana","km":13.83},{"nome":"Cont. Califórnia","km":5.46},{"nome":"Cont. Leste PG","km":27.7},{"nome":"Cont. Norte PG","km":14.65}]},"4":{"sistema":[{"rod":"PR-323","ext":203.76},{"rod":"BR-376","ext":173.9},{"rod":"BR-369","ext":74.0},{"rod":"BR-272","ext":45.3},{"rod":"PR-444","ext":38.78},{"rod":"PR-182","ext":32.48},{"rod":"PR-272","ext":16.09},{"rod":"PR-317","ext":13.2},{"rod":"PR-862","ext":12.64},{"rod":"PR-986","ext":9.9},{"rod":"PR-897","ext":7.47}],"duplicacoes":[{"rod":"PR-323","trechos":27,"km":159.98},{"rod":"BR-376","trechos":5,"km":65.2},{"rod":"PR-272","trechos":1,"km":3.88},{"rod":"BR-272","trechos":2,"km":2.2},{"rod":"BR-369","trechos":1,"km":0.7}],"oaes":[{"rod":"PR-323","n":71},{"rod":"BR-369","n":17},{"rod":"Cont. Norte Londrina","n":16},{"rod":"BR-376","n":15},{"rod":"PR-444","n":13},{"rod":"PR-986","n":8},{"rod":"PR-862","n":6},{"rod":"PR-897","n":4},{"rod":"Cont. Sul Maringá","n":4},{"rod":"PR-272","n":2},{"rod":"Cont. Nova Londrina","n":2},{"rod":"PR-317","n":2},{"rod":"BR-272","n":1},{"rod":"PR-182","n":1}],"diamantes":[{"rod":"PR-323","n":23},{"rod":"PR-444","n":5},{"rod":"BR-376","n":3},{"rod":"PR-272","n":3},{"rod":"Cont. Sul Maringá","n":3},{"rod":"PR-862","n":2},{"rod":"Cont. Norte Londrina","n":2},{"rod":"BR-272","n":1},{"rod":"BR-369","n":1},{"rod":"PR-317","n":1}],"trombetas":[{"rod":"PR-323","n":5},{"rod":"BR-369","n":1},{"rod":"PR-444","n":1},{"rod":"PR-862","n":1},{"rod":"Cont. Sul Maringá","n":1}],"rotatorias":[{"rod":"BR-376","n":6},{"rod":"BR-272","n":4},{"rod":"PR-272","n":3},{"rod":"Cont. Nova Londrina","n":3},{"rod":"BR-369","n":2},{"rod":"Cont. Itaúna do Sul","n":2},{"rod":"PR-182","n":1}],"parclos":[{"rod":"PR-323","n":18},{"rod":"BR-376","n":9},{"rod":"PR-444","n":2},{"rod":"BR-369","n":1},{"rod":"PR-897","n":1},{"rod":"PR-986","n":1}],"passarelas":[{"rod":"PR-323","n":17},{"rod":"BR-376","n":13},{"rod":"BR-369","n":4},{"rod":"PR-444","n":2},{"rod":"PR-182","n":1},{"rod":"PR-272","n":1},{"rod":"PR-897","n":1}],"marginais":[{"rod":"PR-323","km":18.04,"n":17},{"rod":"BR-376","km":10.27,"n":6},{"rod":"BR-369","km":4.9,"n":5},{"rod":"PR-272","km":3.88,"n":1},{"rod":"BR-272","km":1.4,"n":1},{"rod":"PR-444","km":1.0,"n":2}],"ciclovias":[{"rod":"PR-323","n":11,"km":15.87},{"rod":"BR-376","n":7,"km":8.72},{"rod":"PR-272","n":1,"km":3.81},{"rod":"BR-369","n":4,"km":3.52},{"rod":"BR-272","n":1,"km":1.34},{"rod":"PR-444","n":1,"km":0.66}],"faixas":[{"rod":"PR-986","n":2,"km":19.6},{"rod":"BR-272","n":7,"km":18.39},{"rod":"PR-897","n":4,"km":11.4},{"rod":"PR-862","n":2,"km":11.32},{"rod":"PR-272","n":4,"km":10.3},{"rod":"PR-182","n":2,"km":9.01},{"rod":"BR-376","n":4,"km":5.67},{"rod":"BR-369","n":2,"km":1.42}],"contornos":[{"nome":"Cont. Itaúna do Sul","km":5.33},{"nome":"Contorno Norte Londrina*","km":30.1},{"nome":"Cont. Nova Londrina","km":4.43},{"nome":"Contorno Sul Maringá*","km":19.27}]},"5":{"sistema":[{"rod":"BR-369","ext":174.8},{"rod":"BR-163","ext":154.9},{"rod":"PR-317","ext":63.54},{"rod":"PR-158","ext":15.24},{"rod":"BR-467","ext":9.6},{"rod":"PR-467","ext":6.41},{"rod":"Contorno de Peabiru","ext":6.2},{"rod":"PR-977","ext":1.1},{"rod":"PR-978","ext":0.98}],"duplicacoes":[{"rod":"BR-369","trechos":12,"km":170.81},{"rod":"BR-163","trechos":4,"km":58.2},{"rod":"PR-158","trechos":1,"km":9.56}],"oaes":[{"rod":"BR-369","n":33},{"rod":"BR-163","n":14},{"rod":"PR-317","n":7},{"rod":"BR-467","n":8},{"rod":"PR-158","n":4},{"rod":"Cont. de Guaíra","n":3}],"diamantes":[{"rod":"BR-369","n":26},{"rod":"BR-163","n":5},{"rod":"PR-317","n":5},{"rod":"PR-158","n":2},{"rod":"Cont. de Guaíra","n":2},{"rod":"BR-467","n":1}],"trombetas":[{"rod":"BR-369","n":4},{"rod":"BR-163","n":1},{"rod":"Cont. de Guaíra","n":1},{"rod":"PR-317","n":1}],"rotatorias":[{"rod":"BR-163","n":1}],"parclos":[{"rod":"BR-163","n":2},{"rod":"BR-369","n":1}],"passarelas":[{"rod":"BR-369","n":2},{"rod":"PR-317","n":3}],"marginais":[{"rod":"BR-369","km":12.75,"n":5},{"rod":"BR-163","km":4.12,"n":6},{"rod":"BR-467","km":3.12,"n":5}],"ciclovias":[{"rod":"BR-369","n":5,"km":12.0}],"faixas":[],"contornos":[{"nome":"Cont. de Guaíra","km":3.71}]}};

// ══════════════════════════════════════════════
//  FONTES LOCAIS — links para arquivos no computador
// ══════════════════════════════════════════════

function Sidebar({lote,page,onPage,onBack}){return(<div style={{width:185,background:'#fff',borderRight:`1px solid ${V.g2}`,display:'flex',flexDirection:'column',flexShrink:0}}><div style={{padding:'12px 12px 10px',borderBottom:`1px solid ${V.g1}`,display:'flex',alignItems:'center',gap:7}}><div style={{width:24,height:24,background:V.or,borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center'}}><span style={{color:'#fff',fontSize:10,fontWeight:700}}>M</span></div><div style={{fontSize:10.5,fontWeight:700,color:V.br}}>MANTA</div></div><div style={{margin:'8px 8px 0',background:V.orp,border:`1px solid ${V.orm}`,borderRadius:7,padding:'6px 9px'}}><div style={{fontSize:8,fontWeight:700,color:V.or,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:1}}>Lote Ativo</div><div style={{fontSize:10.5,fontWeight:700,color:V.br,lineHeight:1.3}}>{lote.nome}</div><div style={{fontSize:9,color:V.g4,marginTop:2}}>{lote.extensao} · {lote.fase}</div></div><div style={{padding:'9px 7px 2px'}}><div style={{fontSize:8.5,fontWeight:700,color:V.g4,letterSpacing:'.1em',textTransform:'uppercase',padding:'0 6px 4px'}}>Análise</div>{NAV.map((n,i)=>(<div key={n.k} onClick={()=>onPage(n.k)} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 8px',borderRadius:5,fontSize:11,color:page===n.k?V.or:V.g5,fontWeight:page===n.k?600:400,background:page===n.k?V.orp:'transparent',cursor:'pointer',marginBottom:1,transition:'all .11s'}} onMouseEnter={e=>{if(page!==n.k){e.currentTarget.style.background=V.orp;e.currentTarget.style.color=V.or;}}} onMouseLeave={e=>{if(page!==n.k){e.currentTarget.style.background='transparent';e.currentTarget.style.color=V.g5;}}}><span style={{fontSize:9}}>{n.icon}</span>{i+1}. {n.label}</div>))}</div><div style={{marginTop:'auto',padding:10,borderTop:`1px solid ${V.g1}`}}><button onClick={onBack} style={{background:'none',border:'none',color:V.or,fontSize:10,fontWeight:600,cursor:'pointer',fontFamily:'inherit',padding:'2px 0',display:'block'}}>← Trocar lote</button><div style={{fontSize:9,color:V.g4,marginTop:4}}>Base: {lote.data_base}</div></div></div>);}

function TopBar({lote,page}){const PL={dash:'Dashboard CAPEX',fin:'Financeiro',tip:'Tipologias',obras:'Obras PER',manta:'Dados Manta',crono:'Cronograma',consol:'Consolidação Obras'};return(<div style={{background:'#fff',borderBottom:`1px solid ${V.g2}`,padding:'0 17px',height:44,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}><div style={{display:'flex',alignItems:'center',gap:5}}><span style={{fontSize:11,color:V.g4}}>Portal</span><span style={{fontSize:11,color:V.g4}}>›</span><span style={{fontSize:11,fontWeight:600,color:V.g7}}>{lote.sig}</span><span style={{fontSize:11,color:V.g4}}>›</span><span style={{fontSize:11,fontWeight:500,color:V.g7}}>{PL[page]||page}</span></div><div style={{display:'flex',alignItems:'center',gap:8}}><span style={{background:V.orp,border:`1px solid ${V.orm}`,color:V.br2,padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:600}}>{lote.fase}</span><span style={{fontSize:10,color:V.g4}}>Base {lote.data_base}</span></div></div>);}

// ── FatoresQualPage · KPIs qualitativos comparativos L3/L4/L5 ───
// ── FatoresQualLotePage · KPIs qualitativos APENAS do lote selecionado ─


// ── SubSidebar · sidebar vertical de sub-abas com dropdown e colapso ──
// Componente reutilizável: usa em ServicosRodPage, OrcParamPage e qualquer página com sub-abas.
//   props:
//     groups:      [{id, label, items:[{k, badge, l, sub}]}]
//     activeKey:   string  — chave da sub-aba ativa
//     onSelect:    (k)=>void
//     storageKey:  string  — chave do localStorage para persistir colapso/dropdowns

// ── DashFinPage · consolidação 1+2 (Dashboard + Financeiro) ──
// ── ServicosRodPage · KPIs por serviço + breakdown por rodovia ─




// ══════════════════════════════════════════════
//  CAPEX HERO — resumo visual top para TipPage
// ══════════════════════════════════════════════

// ── TipoConsolPage · mesclagem de Tipologias e Tipologia × Rodovia ───
// ── TipoRodoviaPageV2 · 3 blocos: Total + Total×Serviços + Detalhe por Rodovia ─





// ── OBRAS-02 · Totais por Serviço (SICRO consolidado por disciplina) ─

// ── 5.3 · Rodovia × KPIs (consolidação 3+4) ─
// ── SectionCollapse: header dropdown + corpo collapse ──


// ── OBRAS-04 · Orçamento Detalhado p/ Rodovia (rateado) ─

// ── 5.5 · Composições Unitárias por código SICRO ─



// ── fmtBRL helper (full format) ──
const fmtBRL = v => v==null?'—':new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL',minimumFractionDigits:2,maximumFractionDigits:2}).format(v);

// ── Shared table styles ──
const TH={padding:'7px 10px',textAlign:'right',fontSize:10,fontWeight:700,color:'#756F68',background:'#F4F3F1',whiteSpace:'nowrap'};
const THl={...TH,textAlign:'left'};
const TD={padding:'5px 10px',textAlign:'right',fontSize:10.5,fontFamily:'monospace',whiteSpace:'nowrap'};
const TDl={...TD,textAlign:'left',fontFamily:'inherit'};

// ══════════════════════════════════════════════
//  COMPARA PARAMÉTRICO — formato exato do HTML
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  PARAMÉTRICO Manta — puxa PR Vias CU da 5.1 como Valor/unid
//  - Itens com qtde → linha "Valor/km|und" com CU na coluna Soma, componentes em —
//  - Itens sem qtde mas com total global (ex. 1.2.4 Acostamentos) → linha com Total global
//  - Grupos, BDI, itens 100% vazios → omitidos
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  MACRO PLANEJAMENTO — tabela por ano
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  ESTRUTURA DO ORÇAMENTO
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  MANTA PAGE — 4 sub-abas
// ══════════════════════════════════════════════
// ── OrcamentoRevisaoTab · Sub-aba A do Paramétrico Manta ─────
// Tabela 3 colunas: Edital (PR Vias) · Ajuste manual · Manta Revisado
//   - 13 itens de Obras de Ampliação (1.2.x do MEF)
//   - 3 indiretos editáveis (ADM Local, BDI Insumos, BDI Serviços) — placeholders
//   - Ajuste por linha: toggle %/R$ + input numérico
//   - Persistência via localStorage por lote
//   - Exportar/Importar JSON

// Componente de input de ajuste (reutilizável dentro da tabela)

// Mapeamento código MEF → quantidades de OBRAS_ROD (Quadro 2)
// Mapeamento código MEF → quantidades de OBRAS_ROD
// Mapeamento código MEF → quantidades de OBRAS_ROD



// Alias retrocompatível — mantém referências antigas funcionando

// ── MANTA-03 · Macro + Estrutura (consolidação 3+4) ────


// ══════════════════════════════════════════════
//  TIPOLOGIA × RODOVIA PAGE
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  CRONO PAGE
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  CONSOLIDAÇÃO OBRAS — 3 lotes lado a lado
//  Quantidades físicas por tipo de estrutura (fonte: compara_parametrico)
//  Barra 0-100% em cada célula, relativa ao MAIOR valor do próprio lote
// ══════════════════════════════════════════════


// ══════════════════════════════════════════════
//  CONSOL FIN PAGE — Consolidado SICRO c/ BDI por Disciplina (L3 / L4 / L5)
// ══════════════════════════════════════════════
// ── RetigSchematic · esquema fora-de-escala da rodovia ──────



// ── RodoviaContexto · narrativa qualitativa derivada dos dados ──

// ── MatrizDisciplinas · heatmap rodovias × disciplinas (qtd e R$) ──

// ── MatrizTiposObra · rodovias × tipos de obra (duplicação, faixa, OAE, etc.) ──


// ── ObrasConsolPage · página única consolidada (PER + Rodovia + SICRO) ─────







// ══════════════════════════════════════════════
//  GESTÃO DE ORÇAMENTO — KPIs de Obras de Implantação
//  Apenas CAPEX físico de construção (sem OPEX, receita, TIR, tarifa, capital)
//  12 KPIs: Extensão, CAPEX Impl., CAPEX/km, Km Dup, Km Contornos, Km Marg,
//  Km Faixas Adic, Km Cicl, Nº Interseções, Nº OAEs, Nº Passarelas, BDI construção
// ══════════════════════════════════════════════


// ══════════════════════════════════════════════
//  APP ROOT — 2 abas principais + 3 colunas na Orçamentação
// ══════════════════════════════════════════════
// ══════════════════════════════════════════════
//  GESTÃO DO CONHECIMENTO (KB)
//  Mockup navegável — 5 sub-abas:
//  1. Repositório · 2. KB Curada · 3. Busca RAG · 4. Prompts & Regras · 5. Ingestão
//  Conteúdo derivado dos dados reais dos Lotes 3/4/5
// ══════════════════════════════════════════════


// Dados mock derivados dos Lotes 3/4/5 ──────────


const KB_CURATED_BDI=[
  {tipo:'Construção rodoviária',l3:23.39,l4:23.20,l5:23.20,fonte:'MEF / ANTT'},
  {tipo:'Restauração rodoviária',l3:23.39,l4:23.20,l5:23.20,fonte:'MEF / ANTT'},
  {tipo:'Conservação rodoviária',l3:32.07,l4:31.87,l5:31.87,fonte:'MEF / ANTT'},
  {tipo:'Recuperação OAE',l3:27.74,l4:27.55,l5:27.55,fonte:'MEF / ANTT'},
];

const KB_RAG_SUGESTOES=[
  {q:'Qual o CU médio de duplicação nos Lotes PR?',tags:['CU','duplicação','PR']},
  {q:'Quais normas referenciam o BDI de Recuperação de OAE?',tags:['BDI','OAE','norma']},
  {q:'Diferença de BDI entre Lote 3 e Lote 4?',tags:['BDI','L3','L4']},
  {q:'Qual tipologia tem o maior CAPEX agregado nos 3 lotes?',tags:['CAPEX','tipologia']},
  {q:'Quantos km de duplicação foram orçados na BR-376?',tags:['BR-376','duplicação']},
  {q:'Composição SICRO para implantação de OAE em concreto armado',tags:['SICRO','OAE']},
];

const KB_RAG_MOCK_RESPOSTA={
  resposta:'O CU médio ponderado de duplicação nos Lotes PR é de aproximadamente R$ 5,58 M/km no Lote 3 (rateio pró-rata do grupo 1.2.1 do MEF PR Vias). Lote 4 e Lote 5 têm quantidades orçadas (227,4 km e 232,7 km respectivamente), mas o CU pelo rateio do agrupamento requer validação — o MEF desses lotes traz apenas o total de grupo sem discriminação por sub-item. A referência DNIT Vol. 10 (doc-008) sugere CU de R$ 5,2-6,1 M/km para duplicações em relevo ondulado no PR, o que valida a ordem de grandeza do Lote 3.',
  fontes:[
    {doc:'doc-004',label:'MEF Lote 3 — PR Vias',trecho:'Grupo 1.2.1 Duplicações · Total R$ 1.278,68 M · Qtd 132,6 km + 99,02 km contornos'},
    {doc:'doc-008',label:'DNIT · Manual de Custos Vol. 10',trecho:'Tab. 4.3 · CU referencial para duplicação em relevo ondulado · PR: R$ 5,2-6,1 M/km'},
    {doc:'doc-015',label:'BDI Referencial ANTT · 2024',trecho:'BDI Construção rodoviária 23,20% (2024) · 23,39% (base 2023)'},
  ],
  confianca:0.86,
};

const KB_PROMPTS=[
  {id:'p-001',nome:'Análise de CU por tipologia',ativo:true,versao:'v2.1',desc:'Ao receber uma tipologia e contexto geográfico, consultar KB Curada, MEF relevantes e norma DNIT correspondente. Retornar CU com faixa de variação e nível de confiança.',exemplo:'CU de OAE em concreto armado no PR'},
  {id:'p-002',nome:'Comparativo entre lotes',ativo:true,versao:'v1.4',desc:'Comparar quantidades, CUs e CAPEX de uma tipologia entre lotes. Citar sempre a data-base de cada valor. Aplicar fator de atualização quando data-base diferir >6 meses.',exemplo:'Comparar duplicação L3 vs L4 vs L5'},
  {id:'p-003',nome:'Consulta SICRO',ativo:true,versao:'v3.0',desc:'Buscar composição SICRO por código ou descrição. Retornar: insumos, mão-de-obra, equipamentos, CU total. Apontar divergências com MEF quando existirem.',exemplo:'Composição para pavimentação CBUQ faixa C'},
  {id:'p-004',nome:'Justificativa de BDI',ativo:false,versao:'v0.3-draft',desc:'Justificar percentual de BDI adotado com base em normas ANTT, tipo de obra e complexidade. Rascunho — aguardando revisão.',exemplo:'BDI para Recuperação de OAE'},
  {id:'p-005',nome:'Leitura de projeto de engenharia',ativo:true,versao:'v1.0',desc:'Ao ingerir PDF de projeto, extrair: disciplina, estaqueamento, quantidades-chave, OAEs mencionadas, sondagens SPT referenciadas. Popular KB Curada se dado for inédito.',exemplo:'Anteprojeto de duplicação BR-376'},
];

const KB_INGEST_FILA=[
  {id:'f-001',arquivo:'Anteprojeto_BR376_km213-250.pdf',tam:'18,4 MB',status:'extraindo',progresso:42,etapa:'OCR em 412 páginas',fonte:'SharePoint'},
  {id:'f-002',arquivo:'Laudo_Sondagem_SP01-SP47.pdf',tam:'6,2 MB',status:'indexando',progresso:78,etapa:'Gerando embeddings · 61/89 trechos',fonte:'SharePoint'},
  {id:'f-003',arquivo:'Composicao_SICRO_Pavimentacao.xlsx',tam:'1,8 MB',status:'fila',progresso:0,etapa:'Aguardando slot',fonte:'Upload manual'},
  {id:'f-004',arquivo:'Edital_ANTT_Aditivo_02.pdf',tam:'2,3 MB',status:'curado',progresso:100,etapa:'Ingestão concluída · 12 composições novas',fonte:'SharePoint'},
  {id:'f-005',arquivo:'Planta_Geom_L4_contorno_norte.dwg',tam:'42,1 MB',status:'erro',progresso:15,etapa:'Formato DWG: requer conversor · adicionar ao backlog',fonte:'SharePoint'},
];

// Componentes das sub-abas ──────────────────────



// ══════════════════════════════════════════════
//  NORMAS & REFERÊNCIAS — acervo externo para geometria/quantitativos/custos
// ══════════════════════════════════════════════


// ══════════════════════════════════════════════
//  ANÁLISE DESCRITIVA — auto-gerada pelo agente a partir dos orçamentos
//  Segmento completo: Órgão × Tipo Infra × UF × Fase
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  KB 3.3 · COMPARATIVO L3 × L4 × L5
//  Matriz comparativa quantitativa + qualitativa
//  Calculado dinamicamente a partir de DADOS
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  SIMULAÇÕES PARAMÉTRICAS — ferramenta ativa
//  Entrada do usuário → cálculo baseado em coeficientes DNIT/SICRO + lotes PR
// ══════════════════════════════════════════════




// ── DiagnosticoPage · agente validador (varre lotes × seções) ─────

// ── GlossarioPage · termos técnicos DNIT/SICRO/MEF/PER ─────

// ── GuiaPage · guia de uso do portal ─────

// ── NovoOrcamentoPage · motor in-memory baseado no KB ─────
// ═══════════════════════════════════════════════════════════════════════════
// NovoOrcamentoPage v9 · Wizard de 6 etapas
// 1.KPIs · 2.Escopo · 3.Similaridade · 4.Confirmação · 5.Geração · 6.Salvar
// ═══════════════════════════════════════════════════════════════════════════

const KB_EXTRAS_KEY = 'manta-kb-extras-v1';
const ORC_GERADOS_KEY = 'manta-orcamentos-gerados-v1';

// ── Bancos auxiliares ────────────────────────────────────────────────────
const REGIOES_BR = {
  'Norte':       ['AC','AM','AP','PA','RO','RR','TO'],
  'Nordeste':    ['AL','BA','CE','MA','PB','PE','PI','RN','SE'],
  'Centro-Oeste':['DF','GO','MT','MS'],
  'Sudeste':     ['ES','MG','RJ','SP'],
  'Sul':         ['PR','RS','SC'],
};
const UF_REGIAO = Object.fromEntries(Object.entries(REGIOES_BR).flatMap(([r,ufs])=>ufs.map(u=>[u,r])));
const UF_NOMES = {AC:'Acre',AL:'Alagoas',AM:'Amazonas',AP:'Amapá',BA:'Bahia',CE:'Ceará',DF:'Distrito Federal',ES:'Espírito Santo',GO:'Goiás',MA:'Maranhão',MG:'Minas Gerais',MS:'Mato Grosso do Sul',MT:'Mato Grosso',PA:'Pará',PB:'Paraíba',PE:'Pernambuco',PI:'Piauí',PR:'Paraná',RJ:'Rio de Janeiro',RN:'Rio Grande do Norte',RO:'Rondônia',RR:'Roraima',RS:'Rio Grande do Sul',SC:'Santa Catarina',SE:'Sergipe',SP:'São Paulo',TO:'Tocantins'};

// Banco INMET aproximado (mm/ano · dias chuvosos · regime)

// Pré-classificação de UF/contexto/chuva dos 3 lotes oficiais
const META_OFICIAL = {
  '3': {uf:'PR', cidade:'Centro-oeste · entre Cascavel e Toledo', ctxRural:0.65, ctxUrbano:0.20, ctxSerra:0.15},
  '4': {uf:'PR', cidade:'Noroeste · Maringá-Umuarama', ctxRural:0.55, ctxUrbano:0.30, ctxSerra:0.15},
  '5': {uf:'PR', cidade:'Norte/sinuoso · Cornélio Procópio-Apucarana', ctxRural:0.60, ctxUrbano:0.10, ctxSerra:0.30},
};

function loadKbExtras(){
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(localStorage.getItem(KB_EXTRAS_KEY) || '[]'); } catch(e){ return []; }
}
function saveKbExtras(arr){
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(KB_EXTRAS_KEY, JSON.stringify(arr)); } catch(e){}
}
function loadOrcGerados(){
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(localStorage.getItem(ORC_GERADOS_KEY) || '[]'); } catch(e){ return []; }
}
function saveOrcGerados(arr){
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(ORC_GERADOS_KEY, JSON.stringify(arr)); } catch(e){}
}

// ── Features padronizadas (incl. UF, cidade, chuva) ──────────────────────


function cosseno(va, vb){
  if (!va || !vb || va.length !== vb.length) return 0;
  const dot = va.reduce((s,x,i)=>s+x*vb[i], 0);
  const ma = Math.sqrt(va.reduce((s,x)=>s+x*x, 0));
  const mb = Math.sqrt(vb.reduce((s,x)=>s+x*x, 0));
  return (ma>0 && mb>0) ? dot/(ma*mb) : 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// Wizard container
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// 05 · NOVO ESTUDO · Fluxo Vertical Contínuo (v26)
// Substitui o wizard de 6 etapas em telas separadas
// Cards expansíveis em acordeão · auto-avança
// ═══════════════════════════════════════════════════════════════════════════

// ── AccordionCard: container do card colapsável ────────────────────────

// ── Botão "Avançar" padrão ───────────────────────────────────────────────

// ── Helpers de import ───────────────────────────────────────────────────
// importarJSONkpis · agora redireciona para o parser avançado (auto-detect formato)
function importarJSONkpis(texto){
  return importarJSONkpisAvancado(texto);
}


// ═══════════════════════════════════════════════════════════════════════════
// 🔗 INTEGRAÇÃO SHAREPOINT MANTA · híbrido fail-loud
// Funciona offline (sempre) · usa backend se disponível
// ═══════════════════════════════════════════════════════════════════════════

// ── Pastas pré-cadastradas (favoritos Manta) ────────────────────────────

// ── API Client SharePoint (probe + adapter) ─────────────────────────────

function probeSharePointBackend(setBackendStatus){
  SP_API.probe().then(r => setBackendStatus(r.ok ? 'on' : 'off'));
}

// ── Parser JSON inteligente · auto-detecta formato Manta ─────────────────




// ── Função principal · substitui importarJSONkpis antigo ─────────────────

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE · SharePointPicker (lista favoritos + URL custom)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// FIM SHAREPOINT INTEGRAÇÃO
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// FASE 1 · SCHEMAS DOS KPIs E PRESETS DO NOVO ESTUDO (v28.4)
// ═══════════════════════════════════════════════════════════════════════════

// ──────────────────────────────────────────────────────────────────
// 22 KPIs Qualitativos · 4 blocos
// ──────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────
// 18 KPIs Quantitativos · 4 grupos
// ──────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────
// PRESETS · 4 templates selecionáveis (mesclagem · só preenche vazios)
// ──────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────
// Função utilitária · mesclar preset (só campos vazios)
// ──────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// v28.6 · HELPERS DE GERAÇÃO DE RELATÓRIOS
// ═══════════════════════════════════════════════════════════════════════════

// ─── Download de arquivo via blob ───────────────────────────────────────


// ─── Abrir em nova janela e disparar print ─────────────────────────────

// ─── Construir tabela HTML de KPIs com schema ──────────────────────────

// ─── Cosseno entre 2 vetores ───────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────
// v28.5 · PAYLOAD INICIAL · LOTE 1 PR (extraído do PER consolidado)
// Carrega de uma vez 24 quali + 16 quanti com fontes corretas
// ──────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL · Página de fluxo vertical
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// v28.5 · TABELA KPIs ESTILO PLANILHA (zebra · linhas finas · edição inline)
// 4 sub-abas verticais · botoeira no topo · coluna Fonte
// ═══════════════════════════════════════════════════════════════════════════

// ─── Cores de fonte (rastreamento de origem do valor) ─────────────────
const FONTE_KPI_STYLES = {
  manual:   {bg:'#F3F4F6', fg:'#374151', label:'manual'},
  preset:   {bg:'#FEF3C7', fg:'#92400E', label:'preset'},
  import:   {bg:'#DBEAFE', fg:'#1E40AF', label:'import'},
  extraido: {bg:'#D1FAE5', fg:'#065F46', label:'extraído'},
};

// ─── Célula de edição inline (transparente · sem borda visível) ───────

// ─── LinhaTabelaKPI · memo · v28.7 · só re-renderiza quando valor/fonte do PRÓPRIO campo muda
// Otimização crucial: sem isso, editar 1 célula re-renderiza as 40 linhas.
// Com memo + comparação rasa, só re-renderiza a linha cujo valor/fonte mudou.

// ─── Tabela KPIs · estilo Excel ───────────────────────────────────────

// ─── Botoeira superior (ações de import/preset) ──────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// v28.6 · RELATÓRIOS · página com 10 saídas (A1-A5 por estudo + B1-B5 consolidados)
// ═══════════════════════════════════════════════════════════════════════════

// ─── Linha da tabela de relatórios ────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// v28.7 · RELATÓRIOS STANDALONE · página acessível direto pelo menu principal
// Lê estudo atual do localStorage · calcula universoEstudos · renderiza RelatoriosPage
// Funciona INDEPENDENTE de Novo Estudo estar aberto
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// v28.12 · ALUCI-GUARD PLACEHOLDER · Cap 16.3 Pacote A · entrada do menu "08"
// Lê o texto da textarea + roda regex contra registries embutidos
// Versão minimal · 44 ABNT + 31 leis federais + 20 SICRO (subset)
// Para a versão completa, ver /home/claude/aluci-guard/ (Python + JSON)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// CARD 1 · IDENTIFICAÇÃO QUALITATIVA · 14 campos label-acima
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// CARD 2 · IMPORTAR DADOS QUANTITATIVOS · 4 modos (PDF/Excel/JSON/Manual)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// CARD 3 · KPIs FÍSICOS · 8 quantidades editáveis
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// CARD 4 · BUSCAR SIMILARIDADE · Top 5 + drivers
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// CARD 5 · ORÇAMENTO ESTIMADO (Rota A | B + cross-validation)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// CARD 6 · EXPORTAR / SALVAR
// ═══════════════════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════════════════
// OrcamentoConsolidadoPage · soma dos 3 lotes oficiais
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// CriterioSimilaridadePage · explicação do algoritmo + tuning
// ═══════════════════════════════════════════════════════════════════════════

// ── BenchmarkingPage · análises consolidadas L3·L4·L5 ──

// ── ApiStackPage · documentação dos endpoints REST ──


// ══════════════════════════════════════════════
//  OBRAS × RODOVIA PAGE
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  AGENTE IA PAGE — consulta em linguagem natural
// ══════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// AGENTES · catálogo unificado + funções puras + orquestrador
// ═══════════════════════════════════════════════════════════════════════════

// ── Funções puras dos agentes (extraídas de AgenteIAPage e do Wizard) ────




// ── WIZARD agentes (puros) ─────────────────────────────────────────────




function agExportador(ctx, prev){
  return {sub:'Exportador', status:'pronto', msg:'Aguardando ação do usuário (etapa 6)', formatos:['JSON','Excel','PDF','SharePoint']};
}

// ═══════════════════════════════════════════════════════════════════════════
// 🛡️ ALUCI-GUARD · Pacote A · Auditor anti-alucinação
// Implementação JS inline conforme Cap 16.3 + skill /mnt/skills/user/aluci-guard
// Cobre AL-01 (norma/lei) · AL-02 (URL/DOI) · AL-03 (SICRO)
// ═══════════════════════════════════════════════════════════════════════════

// ── Registry local (offline · 100% inline) ──────────────────────────────
// Em produção: alimentar via cron a partir do SharePoint Manta


// SICRO mínimo (placeholder · processo de import documentado em README)
// Para Manta operar 100%, plugar contra sicro_db (planilha SharePoint → JSON via cron)
const SICRO_REGISTRY = {
  '5910301':'Escavação mecanizada material 1ª categoria',
  '5910302':'Escavação mecanizada material 2ª categoria',
  '5912101':'Aterro compactado',
  '5916101':'Concreto fck 25 MPa',
  '5917101':'Aço CA-50',
  '5921101':'Forma de madeira para concreto',
};
const SICRO_COBERTURA_AVISO = 'Registry SICRO contém apenas amostra ilustrativa (6 códigos). Em produção, plugar contra SharePoint Manta. Códigos não localizados podem ser válidos — confirme com SICRO oficial DNIT.';

// ── Padrões regex (espelham C-16.2 do manual) ───────────────────────────
const ALUCI_PADROES = [
  {tipo:'AL-01_abnt',  nome:'Norma ABNT NBR',  regex:/ABNT\s+NBR\s+(\d{3,5})(?:[-:\s]*(\d{4}))?/gi, severidadeBase:'alta'},
  {tipo:'AL-01_lei',   nome:'Lei federal',     regex:/Lei\s+(?:n[ºo°]?\s*)?(\d{1,5})[\.\/](\d{4})/gi, severidadeBase:'alta'},
  {tipo:'AL-02_url',   nome:'URL',             regex:/https?:\/\/[^\s)'"<>]+/g,                       severidadeBase:'media'},
  {tipo:'AL-02_doi',   nome:'DOI',             regex:/10\.\d{4,9}\/[-._;()/:A-Z0-9]+/gi,             severidadeBase:'baixa'},
  {tipo:'AL-03_sicro', nome:'Código SICRO',    regex:/SICRO\s*[-:]?\s*(\d{7})/gi,                    severidadeBase:'alta'},
];

// ── Auditor principal · função pura ───────────────────────────────────────

// ── Agentes individuais para o pipeline (separação por categoria AL) ──



// ═══════════════════════════════════════════════════════════════════════════
// PÁGINA · AluciGuardPage (sub-aba do Catálogo de Agentes · item 08)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// FIM ALUCI-GUARD MOTOR + PÁGINA
// ═══════════════════════════════════════════════════════════════════════════

// ── CATÁLOGO unificado ────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// v28.3 · agValidadorPreenchimento · audita completude de TODOS os campos
// ─────────────────────────────────────────────────────────────────────────
// Espera contexto: {sig, lote, cf, od, ext, indiretos, ajustes, obrasOv,
//                   bdiComp, bdiMeses, bdiCalculado, abcServOv, abcInsLista,
//                   custoDiretoEdital, custoDiretoManta, totalGeralEdital, totalGeralManta,
//                   qual, kpis,  // do Novo Estudo (opcional)
//                   linhasObras, linhasIndiretos, top10ABCServ}
// Retorna: {sub, status, score, secoes:[{label, peso, completo, total, faltas, valores}], 
//           benchmarks:[...], lista_acionavel:[...]}


const AGENTES_CATEGORIAS = {
  analise:    {label:'Análise',    cor:'#1d4ed8'},
  ia:         {label:'IA',         cor:'#7C3AED'},
  wizard:     {label:'Wizard',     cor:'#F4A76A'},
  qualidade:  {label:'Qualidade',  cor:'#16a34a'},
  extracao:   {label:'Extração',   cor:'#dc2626'},
};


// ── Orquestrador ──────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// Página · Catálogo de Agentes (lista todos os 10 agentes + executa pipelines)
// ═══════════════════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════════════════
// 04 · CONHECIMENTO CONSOLIDADO (fusão KB + KPIs + Orçamento Consolidado)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// 06 · ADICIONAR À BASE (extraído da sub-aba B do Novo Orçamento)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// 09 · TOUR & GLOSSÁRIO (fusão Guia + Glossário)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// 00 · SUMÁRIO oficial (HeaderArtefato + visão geral · Padrão 12.4)
// ═══════════════════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════
//  DOCUMENTOS DO LOTE (antigo Repositório, filtrado por lote)
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  EXTRATOR ANTT — 5 etapas
//  1. Upload → 2. Checklist prévio → 3. Extração → 4. Preview → 5. Popular abas
// ══════════════════════════════════════════════

// Mock por lote — retorna dados específicos de cada lote




// ══════════════════════════════════════════════
//  RETIGRÁFICO — diagrama linear esquemático de rodovia
//  Dados gerados pelo retigrafico_adapter.py a partir dos PERs extraídos
//  Visualização-only (sem editor/backend neste mockup)
// ══════════════════════════════════════════════

const PAL_OAE={c:"#9333ea",a:"OAE",l:"OAE (ponte/viaduto)"};
const PAL_SEG={duplicacao:"#8B4A2D",faixasAdicionais:"#D4682A",marginais:"#4B5563",contornos:"#2057A8",ciclovias:"#1B7A50"};
const LBL_SEG={duplicacao:"Duplicação",faixasAdicionais:"Faixa adicional",marginais:"Marginal",contornos:"Contorno",ciclovias:"Ciclovia"};
const SEG_STACK=["duplicacao","faixasAdicionais","marginais","contornos","ciclovias"];
const DEV_ORDER=Object.keys(PAL_DEV);
const LBL_GAP={roadType:"tipo de pista",duplicationSide:"lado de duplicação",barrier:"barreira",existingLighting:"iluminação existente",poles:"postes"};
const kmLbl=km=>{if(km==null||isNaN(km))return"—";const i=Math.trunc(km),m=String(Math.round((km-i)*1000)).padStart(3,"0");return i+"+"+m;};

// Layout Y
const RY={ruler:8,rulerH:24,chipA:70,chipB:110,chipR:12,roadTop:148,roadH:56,roadMid:176,oae:216,segStart:246,segH:11,segGap:16,total:340,gutter:102,inset:10,rightPad:12};
const segYPos=k=>RY.segStart+SEG_STACK.indexOf(k)*RY.segGap;
const PAD_L=RY.gutter+RY.inset;


// Quadro-resumo abaixo de cada faixa: dispositivos por tipo · OAEs · segmentos · gaps






// ══════════════════════════════════════════════
//  SIDEBAR VERTICAL PRINCIPAL (~52px)
//  3 abas principais numeradas: 1. Gestão · 2. Orçamentação · 3. KB
// ══════════════════════════════════════════════
const MAIN_TABS=[
  {k:'orcam', n:1,icon:'◆',l:'Orçamentação'},
  {k:'kb',    n:2,icon:'✦',l:'Gestão do Conhecimento'},
];


// ══════════════════════════════════════════════
//  NAVEGAÇÃO VERTICAL — Sidebars de Formações + Páginas
//  Col 1: Formações (com lotes aninhados)
//  Col 2: Páginas do lote
//  Col 3: Conteúdo
// ══════════════════════════════════════════════

const LOTES_INFO=[
  {k:'3',n:'Lote 3',ext:'569,8 km'},
  {k:'4',n:'Lote 4',ext:'627,5 km'},
  {k:'5',n:'Lote 5',ext:'432,8 km'},
];

// Estudos em andamento — concessões candidatas sendo extraídas

const ETAPA_LABEL=['','Upload','Check-list','Extraindo','Preview','Concluído'];
const ETAPA_COR=[null,{bg:V.g1,c:V.g5},{bg:'#FEF3C7',c:'#D97706'},{bg:V.blb,c:V.bl},{bg:V.orp,c:V.or},{bg:V.gnb,c:V.gn}];




// ══════════════════════════════════════════════
//  APP ROOT — modo=lote ou modo=estudo na Orçamentação
// ══════════════════════════════════════════════
// ── LoteSummaryStrip · barra de análise do lote sempre visível ──

// ── BackendStatus · indicador de conexão com backend ───────────

// ── AgenteRunner · executa agente do backend e mostra output ──


// ══════════════════════════════════════════════════════════════
//  CAPA PAGE — tela inicial com hero banner + cards + guia
// ══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// v20 · CONTEXTO DO LOTE — KPIs físicos + conversões MEF (m²)
// ═══════════════════════════════════════════════════════════════════
//
// Dataset placeholder por lote. Cada KPI carrega rastreabilidade {f}:
//   doc  → documento-fonte
//   pag  → página/aba
//   data → data-base do dado
//   aut  → responsável
// Nos KPIs com cálculo direto (km urbano via travessias_urbanas), o
// dataset traz só os parâmetros que o portal não tem ainda.


// ─── v21 · DECOMPOSIÇÃO POR RODOVIA ────────────────────────
// Por lote, lista de rodovias com km, km urbano (estimado), driver de custo principal
// e share estimado de CAPEX (proxy: km × peso de complexidade).

// ─── v21 · CONCLUSÕES POR LOTE ─────────────────────────────
// Drivers de custo finais identificados, narrativa para fechar a página

// ─── v21 · ESTRUTURAS TÉCNICAS POR TIPOLOGIA ───────────────
// Seção transversal e consumos por km — comparativo MEF (edital) vs Manta (recomendado)

// Pesos de tipologia de interseção (parclo simples = 1.0 de referência)
const PESO_INTERSECAO = {
  trombeta:1.5, 'trombeta_rotatoria':1.7,
  diamante:1.2, 'diamante_rotatoria':1.4,
  parclo:1.0, 'parclo_rotatoria':1.2,
  trevo:2.0, rotatoria:0.8,
};

// Helpers de cálculo
const calcKmUrbano = (lote) => {
  const ctx = CONTEXTO_LOTES[lote.k] || {};
  if(ctx.km_urb_override!=null) return ctx.km_urb_override;
  const tu = lote.dados_manta?.obras_per?.travessias_urbanas || [];
  return tu.reduce((s,t)=>s+(t.ext||0),0);
};
const calcInterseccoesPond = (lote) => {
  const i = lote.dados_manta?.obras_per?.intersecoes || {};
  const t = (i.trombetas||[]).length, d = (i.diamantes||[]).length;
  const p = (i.parclos||[]).length, tr = (i.trevos||[]).length;
  // simplificação: peso médio por categoria (sem subdividir variantes)
  return t*PESO_INTERSECAO.trombeta + d*PESO_INTERSECAO.diamante
       + p*PESO_INTERSECAO.parclo  + tr*PESO_INTERSECAO.trevo;
};
const extKm = (lote) => parseFloat(String(lote.extensao||'0').replace(/[^\d.]/g,''))||0;

// Praticabilidade — escala 0–100 (maior = mais fácil de executar)
// Geotécnica: NSPT alto + solo coeso favorável → bom; saprolítico/coluvio reduz
// Hidrológica: chuva alta → ruim
// Fundiária: % urbano alto → ruim (desapropriação)
// Construtiva: % montanhoso alto → ruim, OAE/km alto → ruim

// Custo da ampliação (não conta TI/REC/MANUT — esses são por km de toda extensão)
const capexAmpliacao = (lote) => {
  const tip = lote.tipologias||[];
  const grupos = ['Ampliação','OAE','Interseções'];
  return tip.filter(t=>grupos.includes(t.g)).reduce((s,t)=>s+(t.v||0),0); // R$ M
};
const capexOAE = (lote) => (lote.tipologias||[]).filter(t=>t.cod==='A9').reduce((s,t)=>s+(t.v||0),0);
const capexPassarela = (lote) => (lote.tipologias||[]).filter(t=>t.cod==='A10').reduce((s,t)=>s+(t.v||0),0);
const capexEdif = (lote) => (lote.tipologias||[]).filter(t=>t.g==='Edificações' && t.cod?.startsWith('E')).reduce((s,t)=>s+(t.v||0),0);

// ─── Modal de Rastreabilidade ────────────────────────────────
function FonteBadge({fonte, onClick}){
  if(!fonte) return null;
  return (<span onClick={onClick} title="Ver rastreabilidade" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:18,height:18,borderRadius:4,background:V.orp,color:V.or,fontSize:10,fontWeight:700,cursor:'pointer',marginLeft:6,border:'1px solid '+V.orm}}>📍</span>);
}

// ─── KPI Card reutilizável ──────────────────────────────────

// ─── Barra horizontal estilizada (para distribuição %) ──────

// ─── Mini bar chart pluviometria ────────────────────────────

// ─── Gauge para Praticabilidade ─────────────────────────────

// ─── v21 · Decomposição por Rodovia (componente) ───────────

// ─── v21 · Caixa de Conclusões ─────────────────────────────

// ─── v21 · SVG · Corte de Pavimento ────────────────────────

// ─── v21 · SVG · Seção OAE ─────────────────────────────────

// ─── v21 · SVG · Passarela ─────────────────────────────────

// ─── v21 · Tabela MEF vs Manta ─────────────────────────────

// ─── v21 · Painel de Estruturas Técnicas (TipoConsolPage) ──

// ═══════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL — CONTEXTO DO LOTE
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// FIM v20 · CONTEXTO DO LOTE
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// v22 · GUIA & GLOSSÁRIO (nova aba dedicada na sidebar do lote)
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// v22 · DECOMPOSIÇÃO POR TIPO DE OBRA (consolidado + matriz)
// ═══════════════════════════════════════════════════════════════════

// Mapeamento código → tipo legível + cor

// Cards consolidados por tipo de obra (para o lote inteiro)

// Matriz rodovia × tipo de obra (km e quantidades)

export default function App(){
  // v28.12 · CSS global · animações Manta (correção P03)
  useEffect(()=>{
    const styleId = 'manta-global-animations';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(4px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
    return () => { const el = document.getElementById(styleId); if (el) el.remove(); };
  },[]);

  const [fullScreen,setFullScreen] = useState(false);
  // Atalho de teclado Ctrl+H para alternar fullscreen
  useEffect(()=>{
    const h = (e)=>{
      if(e.ctrlKey && (e.key==='h'||e.key==='H')){ e.preventDefault(); setFullScreen(s=>!s); }
    };
    window.addEventListener('keydown',h);
    return ()=>window.removeEventListener('keydown',h);
  },[]);
   const [mainTab,setMainTab]=useState('orcam');
  const [modo,setModo]=useState('lote'); // 'lote' | 'estudo'
  const [loteKey,setLoteKey]=useState('3');
  const [page,setPage]=useState('dashfin');
  const [estudos,setEstudos]=useState(ESTUDOS_INIT);
  const [estudoId,setEstudoId]=useState(null);
  const [showCapa,setShowCapa]=useState(true); // tela inicial
  const lote=DADOS[loteKey];
  const estudo=estudos.find(e=>e.id===estudoId);

  const [focus,setFocus] = useState(false);
  const handleSelectLote=(k)=>{
    setModo('lote');
    setLoteKey(k);
    setFocus(false);
    setPage('dashfin');
    setShowCapa(false);
  };
  const handleSelectEstudo=(id)=>{setModo('estudo');setEstudoId(id);};
  const handleNovoEstudo=()=>{
    const n=estudos.length+1;
    const novo={
      id:'est-'+String(n).padStart(2,'0'),
      nome:'Estudo '+String(n).padStart(2,'0'),
      concessao:'Nova concessão',
      criado:new Date().toISOString().slice(0,10),
      etapa:1,
      docTipo:'—',
    };
    setEstudos([...estudos,novo]);
    setModo('estudo');
    setEstudoId(novo.id);
  };

  return(
    <div style={{display:'flex',flexDirection:'column',height:'100vh',fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',background:V.g0}}>
      {/* Botão flutuante de fullscreen — SEMPRE visível, canto sup. direito */}
      <button onClick={()=>setFullScreen(!fullScreen)} title={fullScreen?'Mostrar headers (Ctrl+H)':'Modo foco — esconder headers (Ctrl+H)'} style={{
        position:'fixed',top:8,right:12,zIndex:9999,
        width:38,height:38,borderRadius:'50%',
        background:fullScreen?'#1d4ed8':'#fff',
        color:fullScreen?'#fff':'#1d4ed8',
        border:'2px solid #1d4ed8',
        cursor:'pointer',fontSize:16,fontWeight:700,
        display:'flex',alignItems:'center',justifyContent:'center',
        boxShadow:'0 4px 12px rgba(0,0,0,.18)',
        transition:'all .15s'
      }}>{fullScreen?'⤢':'⤡'}</button>

      {/* TopBar premium — visual Portal Iluminação */}
      {!fullScreen && <div style={{background:'linear-gradient(90deg,#0D1526 0%,#1E2A4A 100%)',borderBottom:'1px solid rgba(255,255,255,.08)',padding:'0 18px',height:40,display:'flex',alignItems:'center',gap:12,flexShrink:0,boxShadow:'0 2px 8px rgba(0,0,0,.25)'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:26,height:26,background:'linear-gradient(135deg,#D4682A,#E8834A)',borderRadius:7,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 2px 6px rgba(212,104,42,.4)'}}><span style={{color:'#fff',fontSize:11,fontWeight:800}}>M</span></div>
          <div style={{display:'flex',alignItems:'baseline',gap:6}}>
            <span style={{fontSize:12,fontWeight:700,color:'#fff',letterSpacing:'.02em'}}>MANTA</span>
            <span style={{fontSize:9,color:'rgba(255,255,255,.45)'}}>Portal Orçamentação · ANTT · PR</span>
          </div>
          <span style={{padding:'2px 8px',background:'rgba(212,104,42,.25)',color:'#F4A76A',fontSize:8,fontWeight:700,borderRadius:10,letterSpacing:'.06em',border:'1px solid rgba(212,104,42,.4)'}}>v23</span>
        </div>
        <span style={{flex:1}}/>
        <BackendStatus/>
        <span style={{fontSize:9,color:'rgba(255,255,255,.3)'}}>backend: <code style={{background:'rgba(255,255,255,.08)',padding:'0 5px',borderRadius:3,fontSize:9,color:'rgba(255,255,255,.5)'}}>cd backend &amp;&amp; start.bat</code></span>
      </div>}

      <div style={{display:'flex',flex:1,overflow:'hidden',minHeight:0}}>
        <MainNavSidebar mainTab={mainTab} setMainTab={setMainTab}/>

{mainTab==='kb' && (
          <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column',minHeight:0}}>
            <KnowledgePage/>
          </div>
        )}

        {mainTab==='orcam' && (
          <div style={{display:'flex',flex:1,overflow:'hidden',minHeight:0}}>
            {/* Capa inicial — esconde sidebars até selecionar lote */}
            {showCapa ? (
              <CapaPage onSelectLote={handleSelectLote}/>
            ) : (
              <>
                <FormacoesSidebar
                  modo={modo} loteKey={loteKey} estudoId={estudoId}
                  estudos={estudos}
                  onSelectLote={handleSelectLote} onSelectEstudo={handleSelectEstudo}
                  onNovoEstudo={handleNovoEstudo}
                  lote={lote}
                  focus={focus} setFocus={setFocus}/>
                <PagesSidebar page={page} onPage={setPage} modo={modo} focus={focus}/>
                <div style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',background:V.g0,minWidth:0}}>
                  {/* Botão voltar para capa */}
                  <div style={{display:'flex',alignItems:'center',gap:8,padding:'5px 14px',background:'linear-gradient(90deg,#1E2A4A,#2D3F6B)',borderBottom:'1px solid rgba(255,255,255,.1)',flexShrink:0}}>
                    <button onClick={()=>setShowCapa(true)} style={{padding:'3px 10px',fontSize:10,fontWeight:600,background:'rgba(255,255,255,.12)',color:'rgba(255,255,255,.8)',border:'1px solid rgba(255,255,255,.2)',borderRadius:4,cursor:'pointer',fontFamily:'inherit'}}>← Capa</button>
                    <span style={{fontSize:9,color:'rgba(255,255,255,.4)'}}>Portal de Orçamentação · ANTT · PR</span>
                  </div>
                  {focus && <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 12px',background:'#fff',borderBottom:'1px solid '+V.g2,fontSize:11}}>
                    <button onClick={()=>setFocus(false)} style={{padding:'5px 12px',fontSize:11,fontWeight:600,background:V.bl,color:'#fff',border:'none',borderRadius:4,cursor:'pointer'}}>◄ Mostrar menu</button>
                    <span style={{color:V.gr}}>Modo foco · sidebars colapsadas para maximizar área de visualização</span>
                  </div>}
                  {!fullScreen && <Breadcrumb modo={modo} lote={lote} page={page} estudo={estudo}/>}
                  {modo==='lote' && !fullScreen && <LoteSummaryStrip lote={lote}/>}
                  {modo==='lote'&&<>
                  {page==='dashfin'&&<DashFinPage       lote={lote}/>}
                  {page==='contexto'&&<ContextoLotePage lote={lote}/>}
                  {page==='servrod'&&<ServicosRodPage   lote={lote}/>}
                  {page==='guiagloss'&&<GuiaGlossPage   lote={lote}/>}
                  {page==='tipoconsol'&&<TipoConsolPage lote={lote}/>}
                  {page==='obrasconsol'&&<ObrasConsolPage lote={lote}/>}
                  {page==='orcparam' &&<OrcParamPage    lote={lote}/>}
                  {page==='crono' &&<CronoPage         lote={lote}/>}
                  {page==='docs'  &&<DocsPage          lote={lote}/>}
                  {page==='agente'  &&<AgenteIAPage      lote={lote}/>}
                </>}
                {modo==='estudo'&&estudo&&<ExtractorPage lote={lote} estudo={estudo}/>}
              </div>
            </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
